import type { ReactNode } from "react";

/**
 * Plan-change confirmation copy for **customers** (see ManagePlan).
 * Each key matches one branch of the upgrade/downgrade × interval matrix.
 */
export const SubscriptionChangeText = {
  monthlyUpgradeText: (proratedAmount?: ReactNode): ReactNode =>
    proratedAmount === undefined ? (
      "Are you sure you want to change your plan? This upgrade will go into effect immediately."
    ) : (
      <>
        Are you sure you want to change your plan? This upgrade will go into effect immediately. You will be charged a
        pro-rated amount of {proratedAmount}.
      </>
    ),

  monthlyDowngradeText: (billingDate: string): ReactNode =>
    `Are you sure you want to change your plan? Your plan will change on your next billing cycle on ${billingDate}.`,

  yearlyUpgradeText: (proratedAmount?: ReactNode): ReactNode =>
    proratedAmount === undefined ? (
      "Are you sure you want to change your plan? This upgrade will go into effect immediately."
    ) : (
      <>
        Are you sure you want to change your plan? This upgrade will go into effect immediately. You will be charged a
        pro-rated amount of {proratedAmount}.
      </>
    ),

  yearlyDowngradeText: (billingDate: string): ReactNode =>
    `Are you sure you want to change your plan? Your plan will change on your next billing cycle on ${billingDate}.`,

  monthlyToYearlyUpgradeText: (amount?: ReactNode): ReactNode =>
    amount === undefined ? (
      "Are you sure you want to change your plan? Your upgrade to the annual plan will go into effect immediately."
    ) : (
      <>
        Are you sure you want to change your plan? Your upgrade to the annual plan will go into effect immediately. You
        will be charged {amount}.
      </>
    ),

  yearlyToMonthlyDowngradeText: (billingDate: string): ReactNode =>
    `Are you sure you want to change your plan? Your plan will change on your next billing cycle on ${billingDate}.`,
};

/**
 * Writer/admin plan-change confirmations (Manage User → Account). Same matrix as `SubscriptionChangeText`:
 * third person (`this user` / they / their). Keys must stay 1:1 with `SubscriptionChangeText`.
 */
export const subscriptionChangeAdminFallbackText = "Are you sure you want to change this user's plan?";

export const SubscriptionChangeAdminText = {
  monthlyUpgradeText: (proratedAmount?: ReactNode): ReactNode =>
    proratedAmount === undefined ? (
      "Are you sure you want to change this user's plan? This upgrade will go into effect immediately."
    ) : (
      <>
        Are you sure you want to change this user's plan? This upgrade will go into effect immediately. They will be
        charged a pro-rated amount of {proratedAmount}.
      </>
    ),

  monthlyDowngradeText: (billingDate: string): ReactNode =>
    `Are you sure you want to change this user's plan? Their plan will change on their next billing cycle on ${billingDate}.`,

  yearlyUpgradeText: (proratedAmount?: ReactNode): ReactNode =>
    proratedAmount === undefined ? (
      "Are you sure you want to change this user's plan? This upgrade will go into effect immediately."
    ) : (
      <>
        Are you sure you want to change this user's plan? This upgrade will go into effect immediately. They will be
        charged a pro-rated amount of {proratedAmount}.
      </>
    ),

  yearlyDowngradeText: (billingDate: string): ReactNode =>
    `Are you sure you want to change this user's plan? Their plan will change on their next billing cycle on ${billingDate}.`,

  monthlyToYearlyUpgradeText: (amount?: ReactNode): ReactNode =>
    amount === undefined ? (
      "Are you sure you want to change this user's plan? Their upgrade to the annual plan will go into effect immediately."
    ) : (
      <>
        Are you sure you want to change this user's plan? Their upgrade to the annual plan will go into effect
        immediately. They will be charged {amount}.
      </>
    ),

  yearlyToMonthlyDowngradeText: (billingDate: string): ReactNode =>
    `Are you sure you want to change this user's plan? Their plan will change on their next billing cycle on ${billingDate}.`,
};
